import { Check, CornerDownRight, MessageCircle, RotateCcw, Send } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'

import type { CommentRecord } from '../../shared/types'
import { ApiClientError, apiFetch } from '../api'

export function CommentPanel({ documentId }: { documentId: string }) {
  const [comments, setComments] = useState<CommentRecord[]>([])
  const [body, setBody] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyBody, setReplyBody] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    apiFetch<CommentRecord[]>(`/api/documents/${documentId}/comments`)
      .then((items) => { if (active) setComments(items) })
      .catch((reason) => { if (active) setError(reason instanceof ApiClientError ? reason.message : '留言加载失败') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [documentId])

  const threads = useMemo(
    () => comments.filter((comment) => !comment.parentId).map((root) => ({ root, replies: comments.filter((comment) => comment.parentId === root.id) })),
    [comments],
  )

  async function post(event: FormEvent) {
    event.preventDefault()
    if (!body.trim()) return
    try {
      const created = await apiFetch<CommentRecord>(`/api/documents/${documentId}/comments`, {
        method: 'POST', body: JSON.stringify({ body: body.trim() }),
      })
      setComments((current) => [...current, created])
      setBody('')
    } catch (reason) { setError(reason instanceof ApiClientError ? reason.message : '留言发布失败') }
  }

  async function reply(event: FormEvent, root: CommentRecord) {
    event.preventDefault()
    if (!replyBody.trim()) return
    try {
      const created = await apiFetch<CommentRecord>(`/api/comments/${root.id}/replies`, {
        method: 'POST', body: JSON.stringify({ body: replyBody.trim() }),
      })
      setComments((current) => [...current, created])
      setReplyBody('')
      setReplyingTo(null)
    } catch (reason) { setError(reason instanceof ApiClientError ? reason.message : '回复失败') }
  }

  async function toggle(root: CommentRecord) {
    try {
      const updated = await apiFetch<CommentRecord>(`/api/comments/${root.id}`, {
        method: 'PATCH', body: JSON.stringify({ resolved: !root.resolved }),
      })
      setComments((current) => current.map((comment) => comment.id === updated.id ? updated : comment))
    } catch (reason) { setError(reason instanceof ApiClientError ? reason.message : '留言状态更新失败') }
  }

  return (
    <aside className="side-panel comment-panel" aria-label="留言面板">
      <div className="panel-heading"><div><p className="eyebrow">DISCUSSION</p><h2>留言</h2></div><span>{threads.length}</span></div>
      <form className="comment-compose" onSubmit={post}>
        <label htmlFor="new-comment" className="sr-only">新留言</label>
        <textarea id="new-comment" aria-label="新留言" maxLength={5000} value={body} onChange={(event) => setBody(event.target.value)} placeholder="留下问题、建议或决定…" />
        <button type="submit" disabled={!body.trim()}><Send size={15} />发布留言</button>
      </form>
      {error ? <p className="panel-error" role="alert">{error}</p> : null}
      {loading ? <p className="muted">正在读取留言…</p> : null}
      {!loading && !threads.length ? <div className="panel-empty"><MessageCircle size={25} /><p>还没有留言。把第一个问题留在这里吧。</p></div> : null}
      <div className="comment-threads">
        {threads.map(({ root, replies }) => (
          <article key={root.id} aria-label={`${root.actor}的留言`} className={root.resolved ? 'resolved' : ''}>
            <header><span className="comment-avatar">{root.actor.slice(0, 1).toUpperCase()}</span><div><strong>{root.actor}</strong><time>{new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(root.createdAt))}</time></div>{root.resolved ? <em><Check size={12} />已解决</em> : null}</header>
            <p>{root.body}</p>
            {replies.length ? <div className="comment-replies">{replies.map((item) => <div key={item.id}><CornerDownRight size={14} /><span><strong>{item.actor}</strong>{item.body}</span></div>)}</div> : null}
            <footer><button type="button" onClick={() => { setReplyingTo(root.id); setReplyBody('') }}>回复</button><button type="button" onClick={() => void toggle(root)}>{root.resolved ? <><RotateCcw size={13} />重新打开留言</> : <><Check size={13} />解决留言</>}</button></footer>
            {replyingTo === root.id ? <form className="reply-compose" onSubmit={(event) => void reply(event, root)}><label htmlFor={`reply-${root.id}`}>回复{root.actor}</label><textarea id={`reply-${root.id}`} value={replyBody} onChange={(event) => setReplyBody(event.target.value)} autoFocus /><div><button type="button" onClick={() => setReplyingTo(null)}>取消</button><button type="submit">发送回复</button></div></form> : null}
          </article>
        ))}
      </div>
    </aside>
  )
}
